import { isValidFieldSchemaType } from 'app/shared/collections/StatDefinition';
import { UnitService } from 'app/shared/unit.service';
import { BaseColorRenderer } from 'app/shared/utilities/color_renderer';
import { VectorHeatMapContext } from 'app/views/view_types/view_utils';
import { IMSAggregator } from './aggregators/aggregator';
import { EntityVisualiser } from './render/entity_visualiser';
import { IMSValueGetter } from './value_getters/value_getter';
import { MultiplierValueGetter } from 'app/views/value_getters/MultiplierValueGetter';

const DEFAULT_SCHEMA_TYPE = 'text';

/**
 * @ngdoc service
 * @name IMSVisualiser
 * @description
 * The IMSVisualiser class is used to visualise a value/stat.
 *
 */
export class IMSVisualiser {
    logicalName: string;
    tableName: string;
    displayName: string;
    colorRenderer: BaseColorRenderer;
    valueGetter: IMSValueGetter;
    readonly _entityVisualiser: EntityVisualiser;
    readonly _heatMapAggregators: IMSAggregator[];
    readonly _parentType: string;
    // The type of data that is displayed by this visualiser. This links back to the type of the
    // dynamic stat which is closely related to the ims object type, e.g. IMS_TYPE__ASSET
    // For dynamic stats this can also be 'number', 'string' and probably other primitive types
    // in the future. This field is now (2020-11-02) used to handle both the
    // foreign key case(dynamic stats) and non-dynamic stats
    readonly _fieldSchemaType: string;
    // e.g. Estimated Movement By Zone
    readonly _sourceDynamicStatDisplayName: string;
    vectorHeatMapContext: VectorHeatMapContext | null;

    constructor(
        logicalName: string,
        tableName: string,
        displayName: string,
        colorRenderer: BaseColorRenderer,
        valueGetter: IMSValueGetter,
        entityVisualiser: EntityVisualiser,
        heatMapAggregators: IMSAggregator[] = null,
        parentType: string = null,
        sourceDynamicStatDisplayName: string = null,
        fieldSchemaType: string = null,
        vectorHeatMapContext: VectorHeatMapContext | null = null,
    ) {
        /**
         * @ngdoc property
         * @name logicalName
         * @propertyOf ims.map.service:IMSVisualiser
         * @description
         * The logical name of this visualiser (eg 'avg_rssi')
         */
        this.logicalName = logicalName;

        /**
         * @ngdoc property
         * @name displayName
         * @propertyOf ims.map.service:IMSVisualiser
         * @description
         * The display name of this visualiser (eg 'Average RSSI')
         */
        this.displayName = displayName;

        /**
         * @ngdoc property
         * @name colorRenderer
         * @propertyOf ims.map.service:IMSVisualiser
         * @description
         * The ColorRenderer instance for this visualiser
         */
        this.colorRenderer = colorRenderer;

        /**
         * @ngdoc property
         * @name valueGetter
         * @propertyOf ims.map.service:IMSVisualiser
         * @description
         * The ValueGetter instance for this visualiser
         */
        this.valueGetter = valueGetter;

        /**
         * @ngdoc property
         * @name tableName
         * @propertyOf ims.map.service:IMSVisualiser
         * @description
         * The string name of source table
         */
        this.tableName = tableName;

        /**
         * @ngdoc property
         * @name _entityVisualiser
         * @propertyOf ims.map.service:IMSVisualiser
         * @description
         * The EntityVisualiser instance for this visualiser
         */
        this._entityVisualiser = entityVisualiser;

        /**
         * @ngdoc property
         * @name _heatMapAggregators
         * @propertyOf ims.map.service:IMSVisualiser
         * @description
         * The IMSAggregator instances used for heat maps. Defaults to entityVisualiser aggregators.
         */
        this._heatMapAggregators = heatMapAggregators;

        /**
         * @ngdoc property
         * @name _parentType
         * @propertyOf ims.map.service:IMSVisualiser
         * @description
         * The parentType used for this visualiser
         */
        this._parentType = parentType;

        // Attempt to default heatMapAggregators to the entityVisualiser aggregators
        if (this._heatMapAggregators === null && this.entityVisualiser !== null) {
            this._heatMapAggregators = this.entityVisualiser.aggregators;
        }

        // If this._heatMapAggregators is still not specified, default to empty list
        if (this._heatMapAggregators === null) {
            this._heatMapAggregators = [];
        }

        if (
            fieldSchemaType !== null &&
            fieldSchemaType !== undefined &&
            !isValidFieldSchemaType(fieldSchemaType)
        ) {
            // programmer error!
            throw new Error(`${fieldSchemaType} is not a valid field schema type!`);
        }

        // if this is a dynamic stat, fieldSchemaType is the foreign key type.
        // if its a non dynamic stat(fieldSchema will be null in this situation), we get the type from the valueGetter.
        // if both are null then we fallback onto the default value which is type text
        this._fieldSchemaType =
            fieldSchemaType ||
            (this.valueGetter !== null ? this.valueGetter.getFieldType() : DEFAULT_SCHEMA_TYPE);

        this.vectorHeatMapContext = vectorHeatMapContext;

        this._sourceDynamicStatDisplayName = sourceDynamicStatDisplayName;
    }

    /**
     * @ngdoc property
     * @name entityVisualiser
     * @propertyOf ims.map.service:IMSVisualiser
     * @description
     * Returns the EntityVisualiser instance for this visualiser
     */
    get entityVisualiser(): EntityVisualiser {
        return this._entityVisualiser;
    }

    /**
     * @ngdoc property
     * @name heatMapAggregators
     * @propertyOf ims.map.service:IMSVisualiser
     * @description
     * Returns the IMSAggregator instances used for heat maps.
     */
    get heatMapAggregators(): IMSAggregator[] {
        return this._heatMapAggregators;
    }

    /**
     * @ngdoc property
     * @name parentType
     * @propertyOf ims.map.service:IMSVisualiser
     * @description
     * Returns the parentType used for this visualiser.
     */
    get parentType(): string {
        return this._parentType;
    }

    /**
     * Get the display name of the source dynamic (potentially a fake) dynamic stat
     * e.g. 'Estimated Movement By Zone'
     * @returns {string} string or null
     */
    get sourceDynamicStatDisplayName(): string {
        return this._sourceDynamicStatDisplayName;
    }

    /**
     * Get the field schema type - applicable for dynamic and non-dynamic stats
     *
     * @returns {string} string
     */
    get fieldSchemaType(): string {
        return this._fieldSchemaType;
    }

    getFilterDisplayName(): string {
        let displayName = this.displayName;
        const hasMultipleUnits = UnitService.hasMultipleUnits(this.valueGetter.units);

        if (!hasMultipleUnits) {
            const units = UnitService.getBaseSuffix(this.valueGetter.units);

            if (units) {
                displayName += ` (${units})`;
            }
        }

        return displayName;
    }

    getValuePlaceholder(chosenUnit?: string): string {
        const placeholder = this.valueGetter.placeholder.trim();
        const units = chosenUnit ? chosenUnit : UnitService.getBaseSuffix(this.valueGetter.units);
        const parts = [];

        if (placeholder) {
            parts.push(placeholder);
        }

        if (units) {
            parts.push(`(${units.trim()})`);
        }

        return parts.join(' ');
    }

    /**
     * Gets the value getter's multiplier if exists
     * @returns {number} number
     */
    getMultiplier(): number | null {
        const labelValueGetter = this._entityVisualiser.labelValueGetter;
        return labelValueGetter instanceof MultiplierValueGetter
            ? labelValueGetter.getMultiplier()
            : null;
    }
}
